import React from 'react';
import { ArrowRight, TrendingDown, TrendingUp, FileCheck, Users, Tag, Truck, Fuel, Bell, IndianRupee, AlertTriangle, Building2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Hero: React.FC = () => {
  const navigate = useNavigate();

  const scrollToDemo = () => {
    const element = document.querySelector('#demo');
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  const stats = [
    { value: "30-35%", label: "Cost Reduction" },
    { value: "500K+", label: "Trust GaadiMatic" },
    { value: "99.9%", label: "Uptime" },
  ];

  return (
    <section className="relative pt-24 sm:pt-32 pb-16 sm:pb-20 lg:pt-40 lg:pb-32 overflow-hidden bg-gray-50">
      <div className="absolute inset-0 bg-gradient-to-br from-primary-50/50 via-transparent to-accent-orange/5" />
      <div className="container-main relative z-10 px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
          <div className="space-y-6 sm:space-y-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-primary-100 rounded-full">
              <span className="w-2 h-2 bg-primary-600 rounded-full animate-pulse" />
              <span className="text-xs sm:text-sm font-medium text-primary-700">Secure. Scalable. Simple.</span>
            </div>
            <div className="space-y-4 sm:space-y-6">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-navy-950 leading-tight font-heading">
                India's All-in-One <span className="text-primary-600">Fleet Management Solution</span>
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-xl leading-relaxed">
                Streamline your employee transport, trucking operations, and supply chain management with a single, powerful platform. Whether you have 10 vehicles or 10,000+, scale seamlessly without the hassle.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button onClick={scrollToDemo} className="btn-primary group w-full sm:w-auto justify-center">
                Request Demo
                <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
              </button>
              <button onClick={() => navigate('/pricing')} className="btn-secondary group w-full sm:w-auto justify-center">
                <Tag className="mr-2 w-5 h-5" />
                View Pricing
              </button>
            </div>
            <div className="flex flex-wrap gap-6 sm:gap-8 pt-4">
              {stats.map((stat, index) => (
                <div key={index} className="text-center sm:text-left">
                  <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-navy-950 font-heading">{stat.value}</div>
                  <div className="text-xs sm:text-sm text-gray-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative mt-8 lg:mt-0">
            {/* Browser chrome */}
            <div className="rounded-2xl shadow-2xl overflow-hidden border border-gray-200 mx-auto max-w-md lg:max-w-none">

              {/* Browser bar */}
              <div className="bg-gray-100 px-3 py-2 flex items-center gap-2 border-b border-gray-200">
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                  <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
                </div>
                <div className="flex-1 bg-white rounded-md px-3 py-0.5 text-[10px] text-gray-400 mx-2">app.gaadimatic.com/dashboard</div>
                <div className="w-2.5 h-2.5 rounded-full bg-green-400 animate-pulse" />
              </div>

              {/* App shell */}
              <div className="flex bg-gray-50" style={{height: '380px'}}>

                {/* Sidebar */}
                <div className="w-10 bg-navy-950 flex flex-col items-center py-3 gap-3 flex-shrink-0">
                  <div className="w-6 h-6 bg-primary-600 rounded-md flex items-center justify-center mb-1">
                    <Truck className="w-3.5 h-3.5 text-white" />
                  </div>
                  {[
                    { icon: TrendingUp, active: true },
                    { icon: Truck, active: false },
                    { icon: FileCheck, active: false },
                    { icon: Users, active: false },
                    { icon: Fuel, active: false },
                    { icon: IndianRupee, active: false },
                  ].map((item, i) => (
                    <div key={i} className={`w-7 h-7 rounded-lg flex items-center justify-center cursor-pointer ${item.active ? 'bg-primary-600' : 'hover:bg-white/10'}`}>
                      <item.icon className={`w-3.5 h-3.5 ${item.active ? 'text-white' : 'text-gray-500'}`} />
                    </div>
                  ))}
                  <div className="mt-auto w-7 h-7 rounded-lg flex items-center justify-center">
                    <Bell className="w-3.5 h-3.5 text-gray-500" />
                  </div>
                </div>

                {/* Main content */}
                <div className="flex-1 overflow-hidden flex flex-col">

                  {/* Top bar */}
                  <div className="bg-white border-b border-gray-100 px-3 py-2 flex items-center justify-between flex-shrink-0">
                    <div>
                      <div className="text-[11px] font-bold text-navy-950">Fleet Overview</div>
                      <div className="text-[9px] text-gray-400">Today · Apr 20, 2026</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1 bg-green-50 px-2 py-0.5 rounded-full">
                        <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                        <span className="text-[9px] text-green-700 font-semibold">247 Live</span>
                      </div>
                      <div className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center">
                        <Building2 className="w-3 h-3 text-gray-500" />
                      </div>
                    </div>
                  </div>

                  {/* Dashboard content */}
                  <div className="flex-1 p-2.5 overflow-hidden space-y-2">

                    {/* KPI row */}
                    <div className="grid grid-cols-4 gap-1.5">
                      {[
                        { label: 'Total Fleet', value: '1,284', sub: '+12 this week', color: 'text-primary-600', bg: 'bg-primary-50', icon: Truck },
                        { label: 'Clients', value: '148', sub: '3 new', color: 'text-violet-600', bg: 'bg-violet-50', icon: Users },
                        { label: 'Trips Today', value: '433', sub: '312 done', color: 'text-blue-600', bg: 'bg-blue-50', icon: FileCheck },
                        { label: 'Revenue', value: '₹17L', sub: 'This month', color: 'text-green-600', bg: 'bg-green-50', icon: IndianRupee },
                      ].map((k, i) => (
                        <div key={i} className="bg-white rounded-lg p-2 border border-gray-100 shadow-sm">
                          <div className={`w-5 h-5 ${k.bg} rounded-md flex items-center justify-center mb-1`}>
                            <k.icon className={`w-3 h-3 ${k.color}`} />
                          </div>
                          <div className={`text-sm font-extrabold ${k.color}`}>{k.value}</div>
                          <div className="text-[8px] text-gray-400 leading-tight">{k.label}</div>
                          <div className="text-[8px] text-gray-300">{k.sub}</div>
                        </div>
                      ))}
                    </div>

                    {/* Middle row: Trips donut + Action items + Fuel */}
                    <div className="grid grid-cols-3 gap-1.5">

                      {/* Trips visual */}
                      <div className="bg-white rounded-lg p-2 border border-gray-100 shadow-sm">
                        <div className="text-[9px] font-bold text-gray-700 mb-1.5">Today's Trips</div>
                        <div className="flex items-center gap-2">
                          <svg viewBox="0 0 36 36" className="w-12 h-12 flex-shrink-0">
                            <circle cx="18" cy="18" r="14" fill="none" stroke="#e5e7eb" strokeWidth="4"/>
                            <circle cx="18" cy="18" r="14" fill="none" stroke="#22c55e" strokeWidth="4" strokeDasharray="63 88" strokeDashoffset="22" strokeLinecap="round"/>
                            <circle cx="18" cy="18" r="14" fill="none" stroke="#3b82f6" strokeWidth="4" strokeDasharray="17 88" strokeDashoffset="-41" strokeLinecap="round"/>
                            <circle cx="18" cy="18" r="14" fill="none" stroke="#f59e0b" strokeWidth="4" strokeDasharray="8 88" strokeDashoffset="-58" strokeLinecap="round"/>
                          </svg>
                          <div className="space-y-0.5">
                            {[
                              { label: 'Done', val: '312', color: 'bg-green-400' },
                              { label: 'Active', val: '87', color: 'bg-blue-400' },
                              { label: 'Pending', val: '34', color: 'bg-amber-400' },
                            ].map((t, i) => (
                              <div key={i} className="flex items-center gap-1">
                                <div className={`w-1.5 h-1.5 rounded-full ${t.color}`} />
                                <span className="text-[8px] text-gray-500 w-10">{t.label}</span>
                                <span className="text-[8px] font-bold text-gray-700">{t.val}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Action Items */}
                      <div className="bg-white rounded-lg p-2 border border-gray-100 shadow-sm">
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="text-[9px] font-bold text-gray-700">Action Items</div>
                          <div className="w-3.5 h-3.5 bg-red-100 rounded-full flex items-center justify-center">
                            <AlertTriangle className="w-2 h-2 text-red-500" />
                          </div>
                        </div>
                        <div className="space-y-1">
                          {[
                            { label: 'Delayed Trips', val: '14', color: 'bg-red-100 text-red-700' },
                            { label: 'On Time', val: '298', color: 'bg-green-100 text-green-700' },
                            { label: 'SOS Alert', val: '3', color: 'bg-orange-100 text-orange-700' },
                            { label: 'Maint. Due', val: '18', color: 'bg-amber-100 text-amber-700' },
                          ].map((a, i) => (
                            <div key={i} className="flex items-center justify-between">
                              <span className="text-[8px] text-gray-500">{a.label}</span>
                              <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded-full ${a.color}`}>{a.val}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Fuel & Maintenance */}
                      <div className="bg-white rounded-lg p-2 border border-gray-100 shadow-sm">
                        <div className="text-[9px] font-bold text-gray-700 mb-1.5">Fuel & Maintenance</div>
                        <div className="space-y-1.5">
                          <div>
                            <div className="flex justify-between mb-0.5">
                              <span className="text-[8px] text-gray-400">Fuel Budget</span>
                              <span className="text-[8px] font-bold text-amber-700">₹2.4L / ₹3.5L</span>
                            </div>
                            <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                              <div className="h-full bg-amber-400 rounded-full" style={{width:'68%'}} />
                            </div>
                          </div>
                          <div>
                            <div className="flex justify-between mb-0.5">
                              <span className="text-[8px] text-gray-400">Service Done</span>
                              <span className="text-[8px] font-bold text-green-700">46 / 64</span>
                            </div>
                            <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                              <div className="h-full bg-green-400 rounded-full" style={{width:'72%'}} />
                            </div>
                          </div>
                          <div className="flex items-center gap-1 mt-1 bg-red-50 rounded px-1.5 py-1">
                            <Bell className="w-2.5 h-2.5 text-red-500" />
                            <span className="text-[8px] text-red-600 font-medium">18 vehicles overdue</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Bottom row: Revenue + P&L */}
                    <div className="grid grid-cols-2 gap-1.5">

                      {/* Customer Revenue */}
                      <div className="bg-white rounded-lg p-2 border border-gray-100 shadow-sm">
                        <div className="text-[9px] font-bold text-gray-700 mb-1.5">Customer Revenue</div>
                        <div className="space-y-1">
                          {[
                            { name: 'Phoenitech Travels', val: '₹8.2L', pct: 100 },
                            { name: 'MediCity Hospitals', val: '₹5.1L', pct: 62 },
                            { name: 'ZipBus India', val: '₹3.8L', pct: 46 },
                          ].map((r, i) => (
                            <div key={i}>
                              <div className="flex justify-between mb-0.5">
                                <span className="text-[8px] text-gray-500 truncate">{r.name}</span>
                                <span className="text-[8px] font-bold text-green-700 ml-1">{r.val}</span>
                              </div>
                              <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                                <div className="h-full bg-green-400 rounded-full" style={{width:`${r.pct}%`}} />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Vehicle P&L */}
                      <div className="bg-white rounded-lg p-2 border border-gray-100 shadow-sm">
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="text-[9px] font-bold text-gray-700">Vehicle P&L</div>
                          <span className="text-[8px] text-gray-300">Top vehicles</span>
                        </div>
                        <div className="space-y-1">
                          {[
                            { id: 'TS09 AB 1234', profit: true, val: '+₹18.4K', pct: 85 },
                            { id: 'TS07 CD 5678', profit: true, val: '+₹12.2K', pct: 60 },
                            { id: 'AP28 EF 9012', profit: false, val: '-₹3.1K', pct: 18 },
                            { id: 'TS11 GH 3456', profit: true, val: '+₹9.8K', pct: 48 },
                          ].map((v, i) => (
                            <div key={i} className="flex items-center gap-1.5">
                              <span className="text-[7px] text-gray-400 w-16 truncate">{v.id}</span>
                              <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${v.profit ? 'bg-primary-400' : 'bg-red-400'}`} style={{width:`${v.pct}%`}} />
                              </div>
                              <span className={`text-[8px] font-bold w-12 text-right ${v.profit ? 'text-primary-600' : 'text-red-500'}`}>{v.val}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>

            {/* Floating Alert */}
            <div className="absolute -top-3 -right-3 sm:-top-4 sm:-right-5 bg-white rounded-xl shadow-card p-2.5 border border-gray-100 animate-pulse hidden sm:block" style={{animationDuration:'3s'}}>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-red-100 rounded-full flex items-center justify-center">
                  <Bell className="w-3.5 h-3.5 text-red-500" />
                </div>
                <div>
                  <div className="text-xs font-semibold text-navy-950">SOS Alert</div>
                  <div className="text-[10px] text-gray-500">Vehicle #TS07</div>
                </div>
              </div>
            </div>

            {/* Floating Cost */}
            <div className="absolute -bottom-3 -left-3 sm:-bottom-4 sm:-left-5 bg-white rounded-xl shadow-card p-2.5 border border-gray-100 hidden sm:block">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center">
                  <TrendingDown className="w-3.5 h-3.5 text-green-600" />
                </div>
                <div>
                  <div className="text-xs font-semibold text-navy-950">-18% Fuel Cost</div>
                  <div className="text-[10px] text-green-600">vs last month</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
